import React, { useState, useEffect } from "react";
import { Box, Typography, Select, MenuItem, SelectChangeEvent } from "@mui/material";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import YearPicker from "../yearPicker";

export type PresetType = "weekly" | "monthly" | "yearly" | "dailyInMonth" | "custom";

interface PresetDatePickerProps {
  value?: [Date | null, Date | null];
  preset?: PresetType;
  onChange?: (range: [Date | null, Date | null], preset: PresetType) => void;
}

const PresetDatePicker: React.FC<PresetDatePickerProps> = ({
  value,
  preset = "weekly",
  onChange,
}) => {
  const [selectedPreset, setSelectedPreset] = useState<PresetType>(preset);
  const [range, setRange] = useState<[Date | null, Date | null]>(value || [null, null]);
  const { allowedDays } = DateRangePicker;

  useEffect(() => {
    setSelectedPreset(preset);
    setRange([null, null]);
  }, [preset]);

  const handlePresetChange = (event: SelectChangeEvent<PresetType>) => {
    const newPreset = event.target.value as PresetType;
    setSelectedPreset(newPreset);
    setRange([null, null]);
    onChange?.([null, null], newPreset);
  };

  const getHoverRange = () => {
    if (selectedPreset === "weekly") return "week";
    if (["monthly", "dailyInMonth"].includes(selectedPreset)) return "month";
    return undefined;
  };

  return (
    <Box display="flex" gap={3} flexDirection={{ xs: "column", md: "row" }}>
      {/* Preset dropdown */}
      <Box>
        <Typography variant="subtitle2" mb={1}>Presets</Typography>
        <Select
          value={selectedPreset}
          onChange={handlePresetChange}
          size="small"
          sx={{
            minWidth: 150,
            width:{
              xs:"100%"
            },
            minHeight: 36,
            ".MuiSelect-select": { display: "flex", alignItems: "center", height: "100%" },
          }}
        >
          <MenuItem value="weekly">Weekly</MenuItem>
          <MenuItem value="monthly">Monthly</MenuItem>
          <MenuItem value="yearly">Yearly</MenuItem>
          <MenuItem value="dailyInMonth">Daily in Month</MenuItem>
          <MenuItem value="custom">Custom</MenuItem>
        </Select>
      </Box>

      {/* Date / Year picker */}
      <Box>
        {["weekly", "monthly", "dailyInMonth", "custom"].includes(selectedPreset) && (
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Select Dates</Typography>
        )}

        {selectedPreset === "yearly" ? (
          <YearPicker
            value={range[0]?.getFullYear() ?? null}
            onChange={(year) => {
              if (!year) {
                setRange([null, null]);
                onChange?.([null, null], "yearly");
                return;
              }
              const start = new Date(year, 0, 1);
              const end = new Date(year, 11, 31);
              setRange([start, end]);
              onChange?.([start, end], "yearly");
            }}
          />
        ) : (
          <DateRangePicker
            placeholder="Select Date Range"
            showWeekNumbers
            format="MM/dd/yyyy"
            value={range[0] && range[1] ? [range[0], range[1]] : null}
            preventOverflow
            onChange={(val) => {
              if (!val) {
                setRange([null, null]);
                onChange?.([null, null], selectedPreset);
                return;
              }

              let [start, end] = val as [Date | null, Date | null];

              if (selectedPreset === "weekly" && start) {
                end = new Date(start);
                end.setDate(start.getDate() + 6);
              }

              if (["monthly", "dailyInMonth"].includes(selectedPreset) && start) {
                start = new Date(start.getFullYear(), start.getMonth(), 1);
                end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
              }

              setRange([start, end]);
              onChange?.([start, end], selectedPreset);
            }}
            hoverRange={getHoverRange()}
            shouldDisableDate={selectedPreset === "weekly" ? allowedDays(7) : undefined}
            style={{ width: 280, zIndex: 1500 }} 
            />
        )}
      </Box>
    </Box>
  );
};

export default PresetDatePicker;
